import { RoleNames, PMaxCore } from 'core';
import {
  AppMenuModel,
  DefaultAppMenuModel,
  DefaultAppMenuItemModel
} from 'components/AppMenuBar';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import {
  isRoles,
  notRoles,
  addOnEnabled,
  inCondition,
  hasFuncs
} from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import {
  faChartBar,
  faCheckSquare,
  faBriefcase,
  faCubes,
  faCog,
  faBullhorn
} from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';

export default function defaultMenus(core: PMaxCore): Array<AppMenuModel> {
  const {
    sysAdmin,
    agencyAdmin,
    agencyOperator,
    agencyReport,
    adsReport,
    agencyManager,
    agencySales,
    reconciliationOfficer,
    beaconAdmin,
    beaconExecutive
  } = RoleNames;
  const reportMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.report.items.performance',
      '/reports/performance',
      notRoles(reconciliationOfficer, beaconAdmin, beaconExecutive)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.report.items.campaignDashboard',
      '/reports/campaign-dashboard',
      isRoles(sysAdmin).or(
        isRoles(agencyAdmin, agencyManager, agencyOperator, agencySales).and(
          addOnEnabled(ADDONFEATURE.REPORT.REPORT_CAMPAIGN_DASHBOARD)
        )
      )
    ),
    new DefaultAppMenuItemModel(
      'appMenus.report.items.reconciliation',
      '/reports/reconciliation',
      isRoles(sysAdmin).or(
        isRoles(agencyAdmin, agencyManager, agencyOperator, agencySales).and(
          addOnEnabled(ADDONFEATURE.COMPANY.AGENCY_PROVIDE_REPORT_TO_WORKFLOW)
        )
      )
    ),
    new DefaultAppMenuItemModel(
      'appMenus.report.items.salesChannelReconciliation',
      '/reports/sales-channel-reconciliation',
      isRoles(sysAdmin, reconciliationOfficer)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.report.items.credit',
      '/reports/credits',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.report.items.storedValue',
      '/stored-value',
      isRoles(sysAdmin).or(
        isRoles(agencyAdmin, agencyManager, agencyOperator).and(
          addOnEnabled(ADDONFEATURE.COMPANY.STORED_VALUE)
        )
      )
    )
  ];
  const reportMenu = new DefaultAppMenuModel(
    'appMenus.report.title',
    reportMenuItems,
    faChartBar
  );

  const companyMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.comapnyManagement.items.agencies',
      '/agencies',
      isRoles(sysAdmin, agencyAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.comapnyManagement.items.advertisers',
      '/advertisers',
      isRoles(sysAdmin, agencyAdmin, agencyOperator)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.comapnyManagement.items.oemAccounts',
      '/oem-accounts',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.comapnyManagement.items.accounts',
      '/accounts',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.comapnyManagement.items.pmp',
      '/pmp',
      isRoles(sysAdmin)
    )
  ];
  const companyMenu = new DefaultAppMenuModel(
    'appMenus.comapnyManagement.title',
    companyMenuItems,
    faBriefcase
  );

  const systemMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.system.items.productGroups',
      '/system/product-groups',
      isRoles(sysAdmin).or(
        isRoles(agencyAdmin, agencyManager, agencyOperator).and(
          addOnEnabled(ADDONFEATURE.COMPANY.PRODUCT_GROUP)
        )
      )
    ),
    // new DefaultAppMenuItemModel('appMenus.system.items.roles', '/system/roles', isRoles(sysAdmin)),
    new DefaultAppMenuItemModel(
      'appMenus.system.items.currencyRate',
      '/system/currency-rate',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.system.items.channelGroups',
      '/system/channel-groups',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.system.items.streamingStatus',
      '/system/streaming-status',
      isRoles(sysAdmin).and(
        inCondition(() => {
          const allowedEmails = [
            'jimmy@tenmax.io',
            'marin@tenmax.io',
            'gary.chen@tenmax.io',
            'tom@tenmax.io',
            'raven.liao@tenmax.io',
            'wei.liu@tenmax.io',
            'henry.yeh@tenmax.io',
            'neil@tenmax.io',
            'mandy.yang@tenmax.io',
            'cathy@tenmax.io'
          ];
          return allowedEmails.includes(
            core.authenticationManager.account?.email ?? ''
          );
        })
      )
    )
    // new DefaultAppMenuItemModel('appMenus.system.items.auditLog', '/system/audit-log', isRoles(sysAdmin))
  ];
  const systemMenu = new DefaultAppMenuModel(
    'appMenus.system.title',
    systemMenuItems,
    faCog
  );

  const advertisementMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.ads.items.orders',
      '/orders',
      notRoles(
        sysAdmin,
        adsReport,
        reconciliationOfficer,
        beaconAdmin,
        beaconExecutive
      )
    ),
    new DefaultAppMenuItemModel(
      'appMenus.ads.items.creatives',
      '/creatives',
      notRoles(
        sysAdmin,
        agencyReport,
        adsReport,
        reconciliationOfficer,
        beaconAdmin,
        beaconExecutive
      )
    ),
    // new DefaultAppMenuItemModel('appMenus.ads.items.creativeTemplates', '/ads/creative-templates', isRoles(agencyAdmin, agencyOperator, agencySales, adsAdmin).and(addOnEnabled(ADDONFEATURE.CREATIVE_STUDIO.DCS_TEMPLATES))),
    new DefaultAppMenuItemModel(
      'appMenus.ads.items.conversion',
      '/conversions',
      notRoles(
        agencyReport,
        adsReport,
        reconciliationOfficer,
        beaconAdmin,
        beaconExecutive
      ).and(addOnEnabled(ADDONFEATURE.CONVERSION_TRACKING.CONV_TRACKING_LIST))
    ),
    new DefaultAppMenuItemModel(
      'appMenus.data.items.customLayouts',
      '/custom-layouts',
      isRoles(sysAdmin)
    )
  ];
  const advertisementMenu = new DefaultAppMenuModel(
    'appMenus.ads.title',
    advertisementMenuItems,
    faCheckSquare
  );

  const dataMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.data.items.audienceSegments',
      '/audience-segments',
      notRoles(
        agencyReport,
        adsReport,
        reconciliationOfficer,
        beaconAdmin,
        beaconExecutive
      )
    ),
    new DefaultAppMenuItemModel(
      'appMenus.data.items.tenmaxSegments',
      '/tenmax-segments/sources',
      isRoles(sysAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.data.items.deviceManagement',
      '/device-management',
      isRoles(sysAdmin)
    )
  ];
  const dataMenu = new DefaultAppMenuModel(
    'appMenus.data.title',
    dataMenuItems,
    faCubes
  );

  const beaconMessageMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.beaconMessageManagement.items.beaconMessages',
      '/beacon-messages',
      isRoles(beaconAdmin, beaconExecutive)
    )
  ];
  const beaconMessageMenu = new DefaultAppMenuModel(
    'appMenus.beaconMessageManagement.title',
    beaconMessageMenuItems,
    faBriefcase
  );

  const beaconCompanyMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.beaconComapnyManagement.items.restaurants',
      '/restaurants',
      isRoles(beaconAdmin)
    ),
    new DefaultAppMenuItemModel(
      'appMenus.beaconComapnyManagement.items.merchants',
      '/merchants',
      isRoles(beaconAdmin, beaconExecutive)
    )
  ];
  const beaconCompanyMenu = new DefaultAppMenuModel(
    'appMenus.beaconComapnyManagement.title',
    beaconCompanyMenuItems,
    faBriefcase
  );

  const announcementMenuItems = [
    new DefaultAppMenuItemModel(
      'appMenus.announcement.items.siteAnnouncement',
      '/site-announcements',
      hasFuncs(Permission.SITE_ANNOUNCEMENT_LIST_READ)
    )
  ];
  const announcementMenu = new DefaultAppMenuModel(
    'appMenus.announcement.title',
    announcementMenuItems,
    faBullhorn
  );

  const menus = _.compact([
    advertisementMenu,
    dataMenu,
    reportMenu,
    companyMenu,
    beaconCompanyMenu,
    beaconMessageMenu,
    announcementMenu,
    systemMenu
  ]);
  return menus;
}
